import { useEffect, useRef, useState } from 'react';

// Counts a leading integer up from 0 when the element scrolls into view.
// Values without a leading number (e.g. "VP-level") render static.
// Returns [ref, displayText]. Reduced-motion / no-IO users see the final value.
export default function useCountUp(raw, { duration = 1400 } = {}) {
  const match = /^(\d+)(.*)$/s.exec(String(raw));
  const target = match ? parseInt(match[1], 10) : null;
  const suffix = match ? match[2] : '';

  const ref = useRef(null);
  const [value, setValue] = useState(target ?? 0);

  useEffect(() => {
    if (target === null) return;
    const el = ref.current;
    if (!el) return;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduce || typeof IntersectionObserver === 'undefined') {
      setValue(target);
      return;
    }

    setValue(0);
    let rafId = 0;
    let start = 0;

    const step = (ts) => {
      if (!start) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      // easeOutCubic
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(eased * target));
      if (p < 1) rafId = requestAnimationFrame(step);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          rafId = requestAnimationFrame(step);
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.4 }
    );

    observer.observe(el);
    return () => {
      cancelAnimationFrame(rafId);
      observer.disconnect();
    };
  }, [target, duration]);

  const display = target === null ? raw : `${value}${suffix}`;
  return [ref, display];
}
